import {
  createElement,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { resolveFieldChromeElement } from '../utils/findingPendingEditsUtils';

const COPILOT_REVIEWING_ATTR = 'data-reviewing';
const WASH_DURATION_MS = 700;

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function clearInlineRelativePosition(element: HTMLElement): void {
  if (element.style.position === 'relative') {
    element.style.removeProperty('position');
  }
}

export function useCopilotReviewArrival({
  isReviewing,
  reviewArrivalAt,
  elementRef,
}: {
  isReviewing: boolean;
  reviewArrivalAt?: number;
  elementRef: RefObject<HTMLElement | null>;
}): {
  washPortal: ReactNode;
} {
  const previousArrivalAtRef = useRef<number | undefined>(undefined);
  const appliedRelativePositionRef = useRef(false);
  const [washTarget, setWashTarget] = useState<HTMLElement | null>(null);
  const [washPlayId, setWashPlayId] = useState<number | null>(null);

  useEffect(() => {
    const element = elementRef.current;

    if (!element) {
      setWashTarget(null);

      return;
    }

    const nextTarget = resolveFieldChromeElement(element);

    setWashTarget(nextTarget);

    if (!isReviewing) {
      nextTarget.removeAttribute(COPILOT_REVIEWING_ATTR);
      if (appliedRelativePositionRef.current) {
        clearInlineRelativePosition(nextTarget);
        appliedRelativePositionRef.current = false;
      }

      previousArrivalAtRef.current = undefined;
      setWashPlayId(null);

      return;
    }

    nextTarget.setAttribute(COPILOT_REVIEWING_ATTR, 'true');
    const computedPosition = window.getComputedStyle(nextTarget).position;

    if (computedPosition === 'static') {
      nextTarget.style.position = 'relative';
      appliedRelativePositionRef.current = true;
    }

    const shouldReplayWash =
      reviewArrivalAt !== undefined &&
      reviewArrivalAt !== previousArrivalAtRef.current;

    previousArrivalAtRef.current = reviewArrivalAt;

    if (!shouldReplayWash || prefersReducedMotion()) {
      setWashPlayId(null);

      return;
    }

    setWashPlayId(reviewArrivalAt);
    const timeout = window.setTimeout(() => {
      setWashPlayId(null);
    }, WASH_DURATION_MS);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [elementRef, isReviewing, reviewArrivalAt]);

  useEffect(() => {
    return () => {
      if (!washTarget) {
        return;
      }

      washTarget.removeAttribute(COPILOT_REVIEWING_ATTR);
      if (appliedRelativePositionRef.current) {
        clearInlineRelativePosition(washTarget);
        appliedRelativePositionRef.current = false;
      }
    };
  }, [washTarget]);

  const washPortal =
    washTarget && washPlayId !== null
      ? createPortal(
          createElement('div', {
            className: 'copilot-review-arrival-overlay',
            'data-copilot-review-wash': '',
          }),
          washTarget,
        )
      : null;

  return { washPortal };
}
